import React, { useEffect, useState } from "react";
import Select from 'react-select'
import DatePicker from 'react-datepicker';
import moment from 'moment';

import Layout from 'components/Layout/Layout';
import ThemedSelect from 'components/ThemedSelect';

import LineChart from "./help/LineChart";
import BarChart from "./help/BarChart";
import FiltersButton from "./help/FiltersButton";

import { fetchTags } from "services/tags";
import { getNotchTypes } from "services/notches";

import axiosInstance from "utils/axiosInstance";
import { useLocalStorage } from "utils/storage";

import 'react-datepicker/dist/react-datepicker.css';
import "./styles.css";

const Statistics = () => {
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const [errors, setErrors] = useState({});
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const [showFilters, setShowFilters] = useState(isMobile ? false : true);
	const [charts, setCharts] = useState([]);

	const [notchTypes, setNotchTypes] = useState([
		{ value: "all", label: "All" }, 
		...getNotchTypes()
	]);

	// TODO: get from dictionary
	const [periods, setPeriods] = useState([
		{ value: "last_seven_days", label: "7 Days" },
		{ value: "last_four_weeks", label: "4 Weeks" },
		{ value: "year_to_date", label: "Year to date" },
		{ value: "custom", label: "Custom" },
	]);

	const [tagOptions, setTagOptions] = useState([]);
	const [tags, setTags] = useLocalStorage('tags', []);

	const defaultForm = {
		period: "last_seven_days",
		date_from: null,
		date_to: null,
		notchType: "all",
		tags: [],
		generateTotalLine: true
	};

	const [form, setForm] = useState({ ...defaultForm });

	const isFormChanged = () => {
		return JSON.stringify(form) !== JSON.stringify(defaultForm);
	};

	useEffect(() => {
		const initializeTags = async () => {
			if (!tags.length) {
				try {
					const fetchedTags = await fetchTags();
					setTags(fetchedTags);
				} catch (error) {
					console.error('Failed to fetch tags:', error);
				}
			}

			const options = tags.map(tag => ({
				value: tag.uid,
				label: tag.name
			}));
			setTagOptions(options);
		};

		const initializeStatistics = async () => {
			getStatistics(form);
		};

		initializeTags();
		initializeStatistics();
	}, []);

	// const handleInputChange = (e) => {
	// 	const { name, value, type, checked } = e.target;
	// 	setForm(prevForm => ({
	// 		...prevForm,
	// 		[name]: type === "checkbox" ? checked : value,
	// 	}));
	// };
	const transformDate = (date) => (date ? moment(date).format('YYYY-MM-DD') : null);


	const getStatistics = async (form) => {
		try {
			const transformedParams = {
				period: form.period,
				type: form.notchType,
				tags: form.tags.map(tag => tag.value),
				date_from: form.period === 'custom' ? transformDate(form.date_from) : null,
				date_to: form.period === 'custom' ? transformDate(form.date_to) : null,
				timezone: timezone
			};

			const response = await axiosInstance.get(`/api/statistics`, { params: transformedParams });

			setCharts(response.data.data ?? []);

		} catch (error) {
			console.error('Failed to get statistics:', error);
		}
	};

	function handleSubmit(e) {
		e.preventDefault();

		const newErrors = {};
		if (form.period === 'custom') {
			if (!form.date_from) {
				newErrors.dateFrom = 'From date is required';
			}

			if (!form.date_to) {
				newErrors.dateTo = 'To date is required';
			}

			if (
				!newErrors.dateFrom &&
				!moment(form.date_to).isAfter(moment(form.date_from), 'day')
			) {
				newErrors.dateFrom = 'From date should be after To date';
			}
		} else {
			setForm(prevForm => ({
				...prevForm,
				date_from: null,
				date_to: null
			}));
		}

		setErrors(newErrors);

		if (Object.keys(newErrors).length > 0) {
			return;
		}

		setShowFilters(false);
		getStatistics(form);
	}

	const getChartColSize = (chart) => {
		if (chart.size) {
			return chart.size;
		}

		switch (chart.type) {
			case 'list': return 4; 
			case 'line': return 6; 
			case 'bar': return 12;
			default: return 6;
		}
	};

	const renderChart = (chart, index) => {
		switch (chart.type) {
			case 'line':
				return <LineChart 
						key={index} 
						title={chart.title}
						data={{ 
							datasets: chart.datasets, 
							labels: chart.labels,
							tooltipLabels: chart.tooltipLabels
						}} 
						payload={chart.payload ?? {}}
						yText={chart.yText}
					/>;
			case 'bar':
				return <BarChart 
						key={index} 
						title={chart.title}
						data={{
							datasets: chart.datasets, 
							labels: chart.labels 
						}} 
					/>;
			case 'list':
				return (
					<div key={index} className="card stats-container-height mobile-auto-heigh">
						<div className="card-header list-header no-background no-border px-2">
							<h3>{chart.title}</h3>
						</div>
						<div className="card-body p-0">
							<ul className="list-group list-group-flush mb-0">
								{chart.datasets.map((stat, i) => (
									<li key={i} className="list-group-item d-flex justify-content-between align-items-center px-2 no-hover">
										<span>{stat.label}</span>
										<span className="badge bg-primary rounded-pill">{stat.value}</span>
									</li>
								))}
							</ul>
						</div>
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<Layout 
			title="Statistics"
			omniButton={isMobile && !showFilters && <FiltersButton
				setShowFilters={setShowFilters}
			/>}
		>
			<div className="row" >

				{/* Filters */}
				{!isMobile || showFilters ? (
					<div className="col-md-4 filters-container">
						<form className="needs-validation" noValidate onSubmit={handleSubmit}>
							<div className="card container-height left-navigation" >

								<div className="card-body px-0">

									<div className="mb-3">
										<label htmlFor="period" className="form-label">Period</label>
										<ThemedSelect
											name="period"
											options={periods}
											className="basic-multi-select"
											classNamePrefix="select"
											value={periods.find(option => option.value === form.period) || null}
											onChange={(selectedOption) => {
												setForm(prevForm => ({
													...prevForm,
													period: selectedOption.value || "week"
												}));
											}}
											placeholder="Select period..."
										/>

										{form.period === 'custom' && (
											<>
												<div className="pt-2 has-validation">
													<DatePicker
														id="date_from"
														name="date_from"
														dateFormat="dd-MM-yyyy"
														placeholderText={'From'}
														selected={form.date_from}
														onChange={(date) =>
															setForm(prev => ({
																...prev,
																date_from: date
															})
															)}
														className={`form-control ${errors.dateFrom ? 'is-invalid' : ''}`}
													/>
													{errors.dateFrom && <div className="invalid-feedback d-block">{errors.dateFrom}</div>}
												</div>
												<div className="pt-2 has-validation">
													<DatePicker
														id="date_to"
														name="date_to"
														dateFormat="dd-MM-yyyy"
														placeholderText={'To'}
														selected={form.date_to}
														onChange={(date) =>
															setForm(prev => ({
																...prev,
																date_to: date
															})
															)}
														className={`form-control ${errors.dateTo ? 'is-invalid' : ''}`}
													/>
													{errors.dateTo && <div className="invalid-feedback d-block">{errors.dateTo}</div>}
												</div>
											</>
										)}

									</div>

									<div className="mb-3">
										<label htmlFor="types" className="form-label">Type</label>
										<ThemedSelect
											name="notchType"
											options={notchTypes}
											className="basic-multi-select"
											classNamePrefix="select"
											value={notchTypes.find(option => option.value === form.notchType) || null}
											onChange={(selectedOption) => {
												setForm(prevForm => ({
													...prevForm,
													notchType: selectedOption.value || "notch"
												}));
											}}
											placeholder="Select type..."
										/>

									</div>

									{tagOptions.length > 0 && (
										<div className="mb-3">
											<label htmlFor="tags" className="form-label">Tags</label>
											<Select
												isMulti
												name="tags"
												options={tagOptions}
												className="basic-multi-select"
												classNamePrefix="select"
												value={form.tags}
												onChange={(selectedOptions) => {
													setForm(prevForm => ({
														...prevForm,
														tags: selectedOptions || []
													}));
												}}
												placeholder="Select tags..."
											/>
										</div>
									)}

									{/* <div className="form-check mb-3">
									<input
										type="checkbox"
										id="generateTotalLine"
										name="generateTotalLine"
										checked={form.generateTotalLine}
										onChange={handleInputChange}
										className="form-check-input"
									/>
									<label htmlFor="generateTotalLine" className="form-check-label">Generate Total Line</label>
								</div> */}
								</div>

								<div className="card-footer text-center border-top form-card-footer-paddings no-background">
									<button
										type="submit"
										className="btn btn-success w-100"

									>
										Apply
									</button>
								</div>

							</div>
						</form>
					</div>
				) : null}

				{/* Stats */}
				{!isMobile || !showFilters ? (
					<div className="col-md-8 omni-button-container" style={{paddingBottom: "32px"}}>
						<div className="row"> 
							{charts.length > 0 && charts.map((chart, index) => (
								<div className={`col-md-${getChartColSize(chart)}`} key={index}>
									{renderChart(chart)}
								</div>
							))}
						</div>
					</div>
					
				) : null}


			</div>
		</Layout>
	);

};

export default Statistics;
